import { apiClient } from "@/services/api"
import { hasMoreRows, pageTotal } from "@/services/listAnswer"
import { convertWeekDayNames, toWeekDayNames, weekDays } from "@/services/weekdays"
import { defineStore } from "pinia"

let lastPage = -1

export const PROJECT_FORM_NAMES = {
  GENERAL: "general",
  CONTACTS: "contacts",
  MATERIALS: "materials",
  MACHINES: "machines",
  ADDITIONAL_EFFORTS: "additionalEfforts",
  USERS: "users",
  FILES: "files",
  USER_POSITIONS: "userPositions",
  CONFIGURATION: "configuration",
  HOURLY_WAGE_REPORTS: "hourlyWageReports",
  CONSTRUCTION_REPORTS: "constructionReports",
  PHASES: "phases",
  SERVICE_CATALOG: "serviceCatalog",
  TIME_TRACKING: "timetracking",
  ACTIVITY: "activity",
}

const _blankProject = () => {
  return {
    id: "",
    name: null,
    alternativeId: null,
    montageStartDate: null,
    montagePlannedEndDate: null,
    note: null,
    color: null,
    address: {
      lat: null,
      lng: null,
      state: null,
      place: null,
      street: null,
      country: null,
      postcode: null,
      houseNumber: null,
    },
    customer: {},
    contacts: [],
    materials: [],
    machines: [],
    additionalEfforts: [],
    categories: [],
    users: [],
    userPositions: null,
    hourlyWageReports: [],
    constructionReports: [],
    phases: [],
    configuration: {
      defaultCurrency: null,
      workDays: weekDays.slice(0, 5),
      hideHourlyWageReportUserNames: true,
      attachHourlyWageReportsToConstructionReports: true,
    },
    createdAt: null,
    createdBy: null,
    lastModifiedAt: null,
    lastModifiedBy: null,
  }
}

/**
 * The wire shape of a project, from the object the UI edits. Every write path — create, update and
 * the personnel-planning colour dialog — maps through here, so a field the backend requires cannot
 * go missing on one path alone.
 *
 * `configuration` is rebuilt rather than mutated: the spread is shallow, so writing the wire shape
 * into `project.configuration.workDays` would put it straight back into the store, and every save
 * after that one would map already-converted names to `undefined` and send `[null, …]` — a 400,
 * and a crash in the tabs that read `workDay.number`.
 *
 * `isLeading` is coerced for the same reason the work days are filtered: the backend declares it
 * non-nullable, so a user that reached the list without the flag — the employee picker adds one
 * from a plain search hit — would be serialized without the field, and the save rejected with
 * `Missing required creator property 'isLeading'`.
 */
export function toProjectRequest(project: any) {
  return {
    ...project,
    customer: project.customer?.id,
    contacts: project.contacts ? project.contacts.map(item => ({ id: item.id, positions: item.positions })) : null,
    users: project.users
      ? project.users.map(item => ({ id: item.id, positions: item.positions, isLeading: !!item.isLeading }))
      : null,
    configuration: {
      ...project.configuration,
      workDays: toWeekDayNames(project.configuration?.workDays),
    },
  }
}

/**
 * A project as its unsaved changes are measured (`useUnsavedChanges`): the shape a save sends, less
 * what no save carries. `categories` is the server's: it follows the hourly-wage reports and is ignored
 * in a save (`services/projectRefresh.ts`). A project with no currency of its own is priced in the
 * workspace's, and the currency select writes that in when it first renders.
 */
export function comparableProject(project: any, workspaceCurrency: string | null | undefined) {
  const request = toProjectRequest(project)
  return {
    ...request,
    categories: null,
    configuration: {
      ...request.configuration,
      defaultCurrency: request.configuration?.defaultCurrency || workspaceCurrency,
    },
  }
}

export const useProjectStore = defineStore("project", {
  state: () => ({
    projects: [],
    project: _blankProject(),
  }),
  getters: {
    getProjects: state => state.projects,
    getProject: state => state.project,
  },
  actions: {
    setProjects(projects) {
      this.projects = [...projects]
    },
    addProjects(projects) {
      projects.forEach(project => this.projects.push(project))
    },
    clearProjects() {
      this.projects = []
    },
    setProject(project) {
      this.project = project
      this.project.configuration.workDays = convertWeekDayNames(project.configuration.workDays)
    },
    clearProject() {
      this.project = _blankProject()
    },
    loadProjectMaterialsWithOrigin({ customerId, page, search, sortBy, sortDesc, selected, itemsPerPage, silent = false }) {
      return apiClient
        .get(`/project/materials/search/${customerId}`, {
          params: {
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
          },
          headers: { tenant: true, authorization: true },
          silent,
        })
        .then(res => {
          return res
        })
    },
    loadProjectAdditionalEffortsWithOrigin({ customerId, page, search, sortBy, sortDesc, selected, itemsPerPage, silent = false }) {
      return apiClient
        .get(`/project/additionalefforts/search/${customerId}`, {
          params: {
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
          },
          headers: { tenant: true, authorization: true },
          silent,
        })
        .then(res => {
          return res
        })
    },
    loadProjectMachinesWithOrigin({ customerId, page, search, sortBy, sortDesc, selected, itemsPerPage, silent = false }) {
      return apiClient
        .get(`/project/machines/search/${customerId}`, {
          params: {
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
          },
          headers: { tenant: true, authorization: true },
          silent,
        })
        .then(res => {
          return res
        })
    },
    /** `silent` keeps a failure off the snackbar, for the reads nobody asked for. */
    async loadProject(id, { silent = false } = {}) {
      return apiClient.get(`/project/${id}`, { headers: { tenant: true, authorization: true }, silent }).then(res => {
        if (res.status === 200) {
          this.setProject(res.data)
        }
        return res
      })
    },
    /**
     * The project as `setProject` would keep it, **returned** and not stored: the page merges it
     * into the copy the user is editing (`mergeFreshProject`) instead of replacing that copy.
     */
    async fetchProject(id: string, { silent = false } = {}) {
      const res = await apiClient.get(`/project/${id}`, { headers: { tenant: true, authorization: true }, silent })
      const project = res.data
      project.configuration.workDays = convertWeekDayNames(project.configuration.workDays)
      return project
    },
    /**
     * Looks up a single project in its **list** shape (with `leadingUsers`, which the detail
     * endpoint does not return) and simply returns it — deliberately without touching `projects`,
     * unlike `loadProjects`, whose result feeds the autocomplete dropdowns. A targeted lookup must
     * not leave every project picker on the page filtered down to that one project.
     *
     * Returns null when the id is unknown or the caller may not access it.
     */
    async findProjectById(id: string) {
      return apiClient
        .get("/project", {
          params: { filterByProjectIds: [id], itemsPerPage: 1 },
          headers: { tenant: true, authorization: true },
        })
        .then(res => res.data?.content?.find(project => project.id === id) ?? null)
    },
    /**
     * How many entries each register of one project holds (`FrontendProjectCountsResponse`), for the
     * overview's unfolded row. A register the caller may not open is null, not 0.
     *
     * Silent: the row says itself that its figures are missing and offers to ask again, so a snackbar
     * on top would report the same failure twice.
     */
    async loadProjectCounts(id: string) {
      return apiClient
        .get(`/project/${id}/counts`, { headers: { tenant: true, authorization: true }, silent: true })
        .then(res => res.data)
    },
    /**
     * One page of projects for a search term, **returned** rather than written into `projects` —
     * exactly the split [findProjectById] makes, and for the same reason: the shared list is what
     * every project picker on the page reads its items from, so a keystroke in the clock's field
     * must not leave the entry dialog behind it showing the three sites that matched.
     *
     * Whether something did not fit is [hasMoreRows] — the endpoint answers with a `Page`, which has no
     * `last` to read — and it is what the picker turns into "verfeinern Sie die Suche".
     *
     * Silent: a search fires on typing, and a flaky connection would otherwise stack one error
     * snackbar per keystroke over a field somebody is still typing in.
     *
     * `page` for the pickers that load on as they are scrolled (`usePagedSearch`); `total` is the
     * project list's own count, which — unlike the employee list's — is a real one.
     */
    async searchProjects({
      search,
      page = 0,
      itemsPerPage,
      excludedProjectIds,
      includedProjectIds,
    }: {
      search?: string
      page?: number
      itemsPerPage?: number
      excludedProjectIds?: string[]
      includedProjectIds?: string[]
    }) {
      return apiClient
        .get("/project", {
          params: {
            page,
            itemsPerPage: itemsPerPage,
            ...(search ? { search: search } : {}),
            ...(excludedProjectIds?.length ? { excludedProjectIds } : {}),
            ...(includedProjectIds?.length ? { filterByProjectIds: includedProjectIds } : {}),
          },
          headers: { tenant: true, authorization: true },
          silent: true,
        })
        .then(res => ({
          items: res.data?.content ?? [],
          hasMore: hasMoreRows(res.data, itemsPerPage),
          total: pageTotal(res.data),
        }))
    },
    /**
     * Every project the caller may see, in pages of [pageSize] — for "Alle auswählen" over a picker that
     * only ever loaded the pages somebody scrolled to. Returned, not stored, like [searchProjects].
     */
    async searchAllProjects({
      excludedProjectIds,
      pageSize = 200,
    }: { excludedProjectIds?: string[]; pageSize?: number } = {}) {
      const all = []
      for (let page = 0; ; page += 1) {
        const answer = await this.searchProjects({ page, itemsPerPage: pageSize, excludedProjectIds })
        all.push(...answer.items)
        if (!answer.hasMore || answer.items.length === 0) return all
      }
    },
    async addPhases(
      projectId: string,
      phases: {
        name: string
        color?: string | null
        startDate: string
        endDate: string
        averageCapacity: any
        capacities: any
      }[]
    ) {
      return apiClient
        .post(`/project/${projectId}/phases`, phases, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setProject(res.data)
          }
          return res
        })
    },
    async updatePhases(
      projectId: string,
      phases: {
        id: string
        name: string
        color?: string | null
        startDate: string
        endDate: string
        averageCapacity: any
        capacities: any
      }[]
    ) {
      return apiClient
        .put(`/project/${projectId}/phases`, phases, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setProject(res.data)
          }
          return res
        })
    },
    async updateProject(project) {
      return apiClient
        .put(`/project/${project.id}`, project, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setProject(res.data)
          }
          return res
        })
    },
    createProject(project, files: File[] = []) {
      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append("project", new Blob([JSON.stringify(project)], { type: "application/json" }))
        files.forEach(file => formData.append("files", file))
        return apiClient
          .post("/project/with-files", formData, { headers: { tenant: true, authorization: true } })
          .then((res: any) => {
            if (res.status === 200) this.setProject(res.data)
            return res
          })
      }
      return apiClient.post("/project", project, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          this.setProject(res.data)
        }
        return res
      })
    },
    loadProjects({
      page,
      search,
      sortBy,
      sortDesc,
      selected,
      itemsPerPage,
      excludedProjectIds,
      includedProjectIds,
      referenceType,
      referenceId,
      archive,
    }: {
      page?: number
      search?: string
      sortBy?: string
      sortDesc?: boolean
      selected?: boolean
      itemsPerPage?: number
      excludedProjectIds?: any[]
      includedProjectIds?: any[]
      referenceType?: string
      referenceId?: string
      archive?: string
    }) {
      return apiClient
        .get("/project", {
          params: {
            ...(page ? { page: page } : {}),
            ...(search ? { search: search } : {}),
            ...(sortBy ? { sortBy: sortBy } : {}),
            ...(sortDesc ? { sortDesc: sortDesc } : {}),
            ...(selected ? { selected: selected } : {}),
            ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
            ...(excludedProjectIds ? { excludedProjectIds: excludedProjectIds } : {}),
            ...(includedProjectIds ? { filterByProjectIds: includedProjectIds } : {}),
            // The "only projects that point at this record" narrowing an overview arrives with.
            // Both or neither — the backend ignores one without the other.
            ...(referenceType && referenceId ? { referenceType, referenceId } : {}),
            // `ARCHIVED` for the overview's archive; nothing lists the projects in use (docs/archiving.md).
            ...(archive ? { archive } : {}),
          },
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            if (page === 0) {
              this.setProjects(res.data.content)
            } else if (lastPage === page) {
              this.setProjects(res.data.content)
            } else if (lastPage === page - 1) {
              this.addProjects(res.data.content)
            } else {
              this.setProjects(res.data.content)
            }
            lastPage = page
          }
          return res
        })
    },
    duplicateProject(id: string, request: { alternativeId: string; name: string }) {
      return apiClient.post(`/project/${id}/duplicate`, request, { headers: { tenant: true, authorization: true } })
    },
  },
})
